import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '../../lib/cn';

/**
 * DropdownMenu — shadcn's Radix menu button, themed onto the brand tokens. The row-level
 * overflow ("…") menu: the W3C APG menu-button pattern (Enter/Space/↓ open with focus on the
 * first item, arrow keys move, typeahead, Escape returns focus to the trigger) comes from Radix.
 *
 * Portal primitive like Select/Dialog: the content renders to `document.body`, so every class on
 * it is `tw:`-prefixed or it would land unstyled in the app (see CLAUDE.md § Portal primitives).
 * Give the trigger a contextual name — "Aktionen für Franka Feld", not "Mehr".
 */
export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

export type DropdownMenuContentProps = React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>;

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 4, align = 'end', ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      align={align}
      className={cn(
        'tw:z-50 tw:min-w-44 tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-popover tw:p-1 tw:font-sans tw:text-popover-foreground tw:shadow-lg',
        className
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = 'DropdownMenuContent';

export interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  /** `danger` for a destructive item (remove, revoke). */
  tone?: 'default' | 'danger';
}

export const DropdownMenuItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Item>, DropdownMenuItemProps>(
  ({ className, tone = 'default', ...props }, ref) => (
    <DropdownMenuPrimitive.Item
      ref={ref}
      data-tone={tone}
      className={cn(
        'tw:relative tw:flex tw:cursor-default tw:select-none tw:items-center tw:gap-2 tw:rounded-md tw:px-2.5 tw:py-1.5 tw:text-sm tw:outline-none tw:transition-colors',
        'tw:data-[highlighted]:bg-accent tw:data-[disabled]:pointer-events-none tw:data-[disabled]:opacity-50',
        tone === 'danger' && 'tw:text-[var(--color-error-600)]',
        className
      )}
      {...props}
    />
  )
);
DropdownMenuItem.displayName = 'DropdownMenuItem';

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator ref={ref} className={cn('tw:-mx-1 tw:my-1 tw:h-px tw:bg-border', className)} {...props} />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('tw:px-2.5 tw:py-1.5 tw:text-xs tw:font-medium tw:text-muted-foreground', className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = 'DropdownMenuLabel';
